/**
 * Welcome Screen for New Users
 * 
 * Displayed when a user is authenticated but has no organization yet.
 * Offers options to:
 * 1. Start onboarding (setup their business)
 * 2. Chat with Echobot to learn about the platform
 * 3. View available packages
 */

import React, { useState } from 'react';
import { 
  Sparkles, 
  Rocket, 
  MessageSquare, 
  Package, 
  ArrowRight,
  Play,
  CheckCircle2,
  Users,
  TrendingUp,
  Zap,
  Bot,
  ChevronDown,
  ChevronUp,
  Globe,
  Building2,
  Settings2,
  Star
} from 'lucide-react';

interface WelcomeScreenProps {
  user: {
    uid: string;
    email: string;
    displayName: string;
  };
  onStartOnboarding: () => void;
  onOpenChat?: () => void;
}

// Package data
const packages = [
  {
    id: 'starter',
    name: 'Starter',
    price: 49,
    description: 'Perfekt für Einzelunternehmer und kleine Teams',
    features: [
      'CRM Grundfunktionen',
      'Bis zu 500 Kontakte',
      'E-Mail Integration',
      'Basic Reporting'
    ],
    popular: false,
    color: 'from-bg-bg-surface to-txt-secondary'
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 99,
    description: 'Für wachsende Teams mit ambitionierten Zielen',
    features: [
      'Alles aus Starter',
      'Unbegrenzte Kontakte',
      'AI Sales Assistant',
      'Automatisierungen',
      'Team Collaboration',
      'API Zugang'
    ],
    popular: true,
    color: 'from-accent to-info'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 249,
    description: 'Maßgeschneidert für große Unternehmen',
    features: [
      'Alles aus Professional',
      'Google Ads Integration',
      'Custom Workflows',
      'Dedizierter Support',
      'SLA Garantie',
      'White-Label Option'
    ],
    popular: false,
    color: 'from-accent to-accent'
  }
];

// Platform features for the showcase
const platformFeatures = [
  {
    icon: TrendingUp,
    title: 'Sales Pipeline',
    description: 'Visualisiere deinen gesamten Verkaufsprozess'
  },
  {
    icon: Bot,
    title: 'AI Assistant',
    description: 'Intelligente Empfehlungen für jeden Deal'
  },
  {
    icon: Zap,
    title: 'Automatisierung',
    description: 'Spare Zeit mit smarten Workflows'
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Arbeite nahtlos mit deinem Team'
  }
];

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ 
  user, 
  onStartOnboarding,
  onOpenChat 
}) => {
  const [showPackages, setShowPackages] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState('');

  // Greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Guten Morgen';
    if (hour < 18) return 'Guten Tag';
    return 'Guten Abend';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-bg-base via-bg-raised to-bg-base text-txt-inverted overflow-y-auto">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-info/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-accent/10 rounded-full blur-3xl animate-pulse delay-500" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 py-12">
        {/* Header */}
        <header className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-accent to-info shadow-card shadow-accent/20 mb-6">
            <Sparkles className="w-10 h-10 text-txt-inverted" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {getGreeting()}, {user.displayName.split(' ')[0]}! 👋
          </h1>
          
          <p className="text-xl text-txt-muted max-w-2xl mx-auto">
            Willkommen bei Business Hub. Lass uns gemeinsam dein Sales-System aufbauen.
          </p>
        </header>

        {/* Main Actions */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {/* Start Onboarding - Primary CTA */}
          <button
            onClick={onStartOnboarding}
            className="group relative bg-gradient-to-br from-accent to-info rounded-2xl p-8 text-left hover:scale-[1.02] transition-all duration-300 shadow-card shadow-accent/20 overflow-hidden"
          >
            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-xl bg-bg-card/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Rocket className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Setup starten</h3>
              <p className="text-info mb-4">
                Richte dein CRM in wenigen Minuten ein. Wir analysieren deine Website und konfigurieren alles automatisch.
              </p>
              <div className="inline-flex items-center text-sm font-semibold">
                Jetzt starten <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
            
            {/* Recommended Badge */}
            <div className="absolute top-4 right-4 bg-bg-card/20 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium">
              ⭐ Empfohlen
            </div>
          </button>

          {/* Chat with Bot */}
          <button
            onClick={() => setIsChatOpen(!isChatOpen)}
            className="group bg-bg-card/50 backdrop-blur-sm border border-strong rounded-2xl p-8 text-left hover:border-strong hover:bg-bg-card/70 transition-all duration-300"
          >
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-success to-accent flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <MessageSquare className="w-7 h-7" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Mit Echo sprechen</h3>
            <p className="text-txt-muted mb-4">
              Unser AI-Assistent beantwortet alle Fragen zur Plattform und hilft dir bei der Entscheidung.
            </p>
            <div className="inline-flex items-center text-sm font-semibold text-success">
              Chat öffnen <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          {/* View Packages */}
          <button
            onClick={() => setShowPackages(!showPackages)}
            className="group bg-bg-card/50 backdrop-blur-sm border border-strong rounded-2xl p-8 text-left hover:border-strong hover:bg-bg-card/70 transition-all duration-300"
          >
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-accent to-accent flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Package className="w-7 h-7" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Packages ansehen</h3>
            <p className="text-txt-muted mb-4">
              Entdecke unsere Pakete und finde das passende für dein Business.
            </p>
            <div className="inline-flex items-center text-sm font-semibold text-accent">
              {showPackages ? 'Ausblenden' : 'Packages zeigen'}
              {showPackages ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
            </div>
          </button>
        </div>

        {/* Packages Section (Expandable) */}
        {showPackages && (
          <div className="mb-12 animate-in slide-in-from-top duration-300">
            <h2 className="text-2xl font-bold text-center mb-8">Wähle dein Package</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`relative rounded-2xl p-6 border ${
                    pkg.popular 
                      ? 'bg-gradient-to-br from-accent/10 to-accent/10 border-accent/50' 
                      : 'bg-bg-card/50 border-strong'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-accent to-info rounded-full px-4 py-1 text-xs font-bold">
                      BELIEBT
                    </div>
                  )}
                  
                  <h3 className="text-xl font-bold mb-1">{pkg.name}</h3>
                  <p className="text-txt-muted text-sm mb-4">{pkg.description}</p>
                  
                  <div className="mb-4">
                    <span className="text-4xl font-bold">€{pkg.price}</span>
                    <span className="text-txt-muted">/Monat</span>
                  </div>
                  
                  <ul className="space-y-2 mb-6">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm">
                        <CheckCircle2 className="w-4 h-4 text-success mr-2 flex-shrink-0" />
                        <span className="text-txt-muted">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    onClick={onStartOnboarding}
                    className={`w-full py-3 rounded-xl font-semibold transition-all ${
                      pkg.popular
                        ? 'bg-gradient-to-r from-accent to-info hover:from-accent hover:to-info'
                        : 'bg-bg-card hover:bg-bg-card'
                    }`}
                  >
                    Auswählen
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat Modal */}
        {isChatOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-bg-card rounded-2xl w-full max-w-lg overflow-hidden shadow-heavy animate-in zoom-in-95 duration-200">
              {/* Chat Header */}
              <div className="bg-gradient-to-r from-success to-accent p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-bg-card/20 flex items-center justify-center mr-3">
                    <Bot className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold">Echo - AI Assistent</h3>
                    <p className="text-success text-sm">Immer für dich da</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsChatOpen(false)}
                  className="text-txt-inverted/80 hover:text-txt-inverted transition-colors"
                >
                  ✕
                </button>
              </div>
              
              {/* Chat Messages */}
              <div className="h-80 p-4 overflow-y-auto">
                <div className="bg-bg-card/50 rounded-2xl rounded-tl-sm p-4 mb-4 max-w-[80%]">
                  <p className="text-sm">
                    Hallo {user.displayName.split(' ')[0]}! 👋 Ich bin Echo, dein AI-Assistent.
                  </p>
                </div>
                <div className="bg-bg-card/50 rounded-2xl rounded-tl-sm p-4 mb-4 max-w-[80%]">
                  <p className="text-sm mb-3">
                    Ich helfe dir gerne bei Fragen zu Business Hub. Hier sind einige Themen, über die wir sprechen können:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button className="bg-bg-card hover:bg-bg-surface rounded-full px-3 py-1 text-xs transition-colors">
                      Was ist Business Hub?
                    </button>
                    <button className="bg-bg-card hover:bg-bg-surface rounded-full px-3 py-1 text-xs transition-colors">
                      Welches Package passt zu mir?
                    </button>
                    <button className="bg-bg-card hover:bg-bg-surface rounded-full px-3 py-1 text-xs transition-colors">
                      Wie funktioniert das Onboarding?
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Chat Input */}
              <div className="p-4 border-t border-strong">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    placeholder="Stelle eine Frage..."
                    className="flex-1 bg-bg-card rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30"
                  />
                  <button className="bg-success hover:bg-success-soft rounded-xl px-4 transition-colors">
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Feature Showcase */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-center mb-8">Was dich erwartet</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {platformFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-bg-card/30 backdrop-blur-sm border border-strong/50 rounded-xl p-5 text-center hover:bg-bg-card/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-bg-card/50 flex items-center justify-center mx-auto mb-3">
                  <feature.icon className="w-6 h-6 text-info" />
                </div>
                <h4 className="font-semibold mb-1">{feature.title}</h4>
                <p className="text-txt-muted text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Setup Steps Preview */}
        <div className="bg-bg-card/30 backdrop-blur-sm border border-strong/50 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-center mb-8">So einfach geht's</h2>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-info/20 text-info flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <div className="w-12 h-12 rounded-xl bg-bg-card/50 flex items-center justify-center mx-auto mb-3">
                <Globe className="w-6 h-6 text-txt-muted" />
              </div>
              <h4 className="font-semibold mb-1">Website eingeben</h4>
              <p className="text-txt-muted text-sm">Wir analysieren deine Website automatisch</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-info/20 text-info flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <div className="w-12 h-12 rounded-xl bg-bg-card/50 flex items-center justify-center mx-auto mb-3">
                <Building2 className="w-6 h-6 text-txt-muted" />
              </div>
              <h4 className="font-semibold mb-1">Branche bestätigen</h4>
              <p className="text-txt-muted text-sm">Wir erkennen deine Branche automatisch</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-info/20 text-info flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <div className="w-12 h-12 rounded-xl bg-bg-card/50 flex items-center justify-center mx-auto mb-3">
                <Package className="w-6 h-6 text-txt-muted" />
              </div>
              <h4 className="font-semibold mb-1">Package wählen</h4>
              <p className="text-txt-muted text-sm">Wähle das passende Paket für dich</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-success/20 text-success flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                ✓
              </div>
              <div className="w-12 h-12 rounded-xl bg-bg-card/50 flex items-center justify-center mx-auto mb-3">
                <Settings2 className="w-6 h-6 text-txt-muted" />
              </div>
              <h4 className="font-semibold mb-1">Fertig!</h4>
              <p className="text-txt-muted text-sm">Dein CRM ist einsatzbereit</p>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <button
              onClick={onStartOnboarding}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-accent to-info rounded-xl font-bold text-lg hover:from-accent hover:to-info transition-all shadow-card shadow-accent/20"
            >
              <Play className="w-5 h-5 mr-2" />
              Jetzt loslegen
            </button>
          </div>
        </div>

        {/* Footer */}
        <footer className="text-center text-txt-tertiary text-sm">
          <p>© 2024 Business Hub. AI-Powered Sales Intelligence.</p>
          <p className="mt-2">
            Du bist eingeloggt als <span className="text-txt-muted">{user.email}</span>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default WelcomeScreen;

